import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "../../components/Navbar";
import "./BookingPage.css";

const BookingPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    userName: "",
    userEmail: "",
    userPhone: "",
    meetingPurpose: "",
    checkInDate: "",
    checkInTime: "09:00",
    checkOutTime: "10:00",
    numberOfGuests: "1",
    specialRequests: "",
    paymentMethod: "card",
    agreeToTerms: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load room data
  useEffect(() => {
    try {
      const rooms = JSON.parse(localStorage.getItem("meetingRooms") || "[]");
      const foundRoom = rooms.find((r) => r.id === roomId || r._id === roomId);
      if (foundRoom) {
        setRoom(foundRoom);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error loading room:", error);
      setLoading(false);
    }
  }, [roomId]);

  // Load user data
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (user.name || user.email || user.phone) {
        setFormData((prev) => ({
          ...prev,
          userName: user.name || "",
          userEmail: user.email || "",
          userPhone: user.phone || "",
        }));
      }
    } catch (error) {
      console.error("Error loading user data:", error);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.userName.trim()) newErrors.userName = "Name is required";
      if (!formData.userEmail.trim()) newErrors.userEmail = "Email is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.userEmail))
        newErrors.userEmail = "Invalid email format";
      if (!formData.userPhone.trim()) newErrors.userPhone = "Phone is required";
      else if (!/^\d{10}$/.test(formData.userPhone.replace(/\D/g, "")))
        newErrors.userPhone = "Phone must be 10 digits";
      if (!formData.meetingPurpose.trim()) newErrors.meetingPurpose = "Meeting purpose is required";
    } else if (step === 2) {
      if (!formData.checkInDate) newErrors.checkInDate = "Check-in date is required";
      if (!formData.checkInTime) newErrors.checkInTime = "Check-in time is required";
      if (!formData.checkOutTime) newErrors.checkOutTime = "Check-out time is required";

      if (formData.checkInTime && formData.checkOutTime) {
        const [inH, inM] = formData.checkInTime.split(":").map(Number);
        const [outH, outM] = formData.checkOutTime.split(":").map(Number);
        const inMinutes = inH * 60 + inM;
        const outMinutes = outH * 60 + outM;

        if (outMinutes <= inMinutes) {
          newErrors.checkOutTime = "Check-out time must be after check-in time";
        }
      }
    } else if (step === 3) {
      if (!formData.numberOfGuests) newErrors.numberOfGuests = "Number of guests is required";
    } else if (step === 4) {
      if (!formData.paymentMethod) newErrors.paymentMethod = "Payment method is required";
    } else if (step === 5) {
      if (!formData.agreeToTerms) newErrors.agreeToTerms = "You must agree to terms and conditions";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 5));
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    try {
      console.log("Booking submitted:", {
        ...formData,
        roomId: room._id || room.id,
        roomName: room.name || room.roomName,
      });

      toast.success("Booking confirmed successfully!");
      setTimeout(() => {
        navigate("/meetings/rooms");
      }, 2000);
    } catch (error) {
      console.error("Booking error:", error);
      toast.error(error.message || "Failed to confirm booking");
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateDuration = () => {
    if (!formData.checkInTime || !formData.checkOutTime) return 0;
    const [inH, inM] = formData.checkInTime.split(":").map(Number);
    const [outH, outM] = formData.checkOutTime.split(":").map(Number);
    const inMinutes = inH * 60 + inM;
    const outMinutes = outH * 60 + outM;
    return (outMinutes - inMinutes) / 60;
  };

  const duration = calculateDuration();
  const pricePerHour = room?.pricePerHour || 0;
  const totalPrice = duration * pricePerHour;

  if (loading) {
    return (
      <div className="booking-page">
        <Navbar />
        <div className="booking-loading">Loading...</div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="booking-page">
        <Navbar />
        <div className="booking-error">Room not found</div>
      </div>
    );
  }

  return (
    <div className="booking-page">
      <Navbar />

      <div className="booking-container">
        {/* Left Side - Room Image */}
        <div className="booking-image-section">
          <img
            src={room.images?.[0] || "https://via.placeholder.com/500x700"}
            alt={room.name || "Meeting Room"}
            className="booking-room-image"
          />
          <div className="booking-room-info">
            <h2>{room.name || room.roomName || "Meeting Room"}</h2>
            <p>📍 {room.location?.city || room.city || "Location"}</p>
            <p>👥 {room.capacity || "N/A"} people</p>
            <p className="price">₹{new Intl.NumberFormat("en-IN").format(pricePerHour)}/hour</p>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="booking-form-section">
          {/* Header */}
          <div className="booking-header">
            <button className="back-button" onClick={() => navigate(-1)}>
              ← Go Back
            </button>
            <h1>Book Meeting Room</h1>
          </div>

          {/* Progress Indicator */}
          <div className="booking-progress">
            <div className="progress-steps">
              {[1, 2, 3, 4, 5].map((step) => (
                <div
                  key={step}
                  className={`progress-step ${currentStep >= step ? "active" : ""} ${
                    currentStep === step ? "current" : ""
                  }`}
                  onClick={() => {
                    if (step < currentStep) {
                      setCurrentStep(step);
                    }
                  }}
                >
                  <span className="step-number">{step}</span>
                  <span className="step-label">
                    {step === 1 && "Basic Info"}
                    {step === 2 && "Property"}
                    {step === 3 && "Check-in/Out"}
                    {step === 4 && "Pricing"}
                    {step === 5 && "Payment"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="booking-content">
            {/* Step 1: Basic Info */}
            {currentStep === 1 && (
              <div className="booking-step">
                <h3>Basic Info - User Details</h3>

                <div className="form-group">
                  <label htmlFor="userName">Full Name *</label>
                  <input
                    type="text"
                    id="userName"
                    name="userName"
                    value={formData.userName}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className={errors.userName ? "error" : ""}
                  />
                  {errors.userName && <span className="error-message">{errors.userName}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="userEmail">Email Address *</label>
                  <input
                    type="email"
                    id="userEmail"
                    name="userEmail"
                    value={formData.userEmail}
                    onChange={handleInputChange}
                    placeholder="Enter your email"
                    className={errors.userEmail ? "error" : ""}
                  />
                  {errors.userEmail && <span className="error-message">{errors.userEmail}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="userPhone">Phone Number *</label>
                  <input
                    type="tel"
                    id="userPhone"
                    name="userPhone"
                    value={formData.userPhone}
                    onChange={handleInputChange}
                    placeholder="Enter 10-digit phone number"
                    className={errors.userPhone ? "error" : ""}
                  />
                  {errors.userPhone && <span className="error-message">{errors.userPhone}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="meetingPurpose">Meeting Purpose *</label>
                  <textarea
                    id="meetingPurpose"
                    name="meetingPurpose"
                    value={formData.meetingPurpose}
                    onChange={handleInputChange}
                    placeholder="Describe the purpose of your meeting"
                    rows="4"
                    className={errors.meetingPurpose ? "error" : ""}
                  />
                  {errors.meetingPurpose && (
                    <span className="error-message">{errors.meetingPurpose}</span>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Property */}
            {currentStep === 2 && (
              <div className="booking-step">
                <h3>Property - Room Details</h3>

                <div className="property-info-card">
                  <div className="property-details-grid">
                    <div className="property-detail">
                      <span className="detail-label">Room Name:</span>
                      <span className="detail-value">{room.name || room.roomName}</span>
                    </div>
                    <div className="property-detail">
                      <span className="detail-label">Location:</span>
                      <span className="detail-value">{room.location?.city || room.city || "N/A"}</span>
                    </div>
                    <div className="property-detail">
                      <span className="detail-label">Capacity:</span>
                      <span className="detail-value">{room.capacity || "N/A"} people</span>
                    </div>
                    <div className="property-detail">
                      <span className="detail-label">Price:</span>
                      <span className="detail-value">
                        ₹{new Intl.NumberFormat("en-IN").format(pricePerHour)}/hour
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Check-in / Check-out */}
            {currentStep === 3 && (
              <div className="booking-step">
                <h3>Check-in / Check-out - Date and Time</h3>

                <div className="form-group">
                  <label htmlFor="checkInDate">Check-in Date *</label>
                  <input
                    type="date"
                    id="checkInDate"
                    name="checkInDate"
                    value={formData.checkInDate}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split("T")[0]}
                    className={errors.checkInDate ? "error" : ""}
                  />
                  {errors.checkInDate && <span className="error-message">{errors.checkInDate}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="checkInTime">Check-in Time *</label>
                    <input
                      type="time"
                      id="checkInTime"
                      name="checkInTime"
                      value={formData.checkInTime}
                      onChange={handleInputChange}
                      className={errors.checkInTime ? "error" : ""}
                    />
                    {errors.checkInTime && <span className="error-message">{errors.checkInTime}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="checkOutTime">Check-out Time *</label>
                    <input
                      type="time"
                      id="checkOutTime"
                      name="checkOutTime"
                      value={formData.checkOutTime}
                      onChange={handleInputChange}
                      className={errors.checkOutTime ? "error" : ""}
                    />
                    {errors.checkOutTime && <span className="error-message">{errors.checkOutTime}</span>}
                  </div>
                </div>

                {duration > 0 && (
                  <div className="duration-info">
                    <p>
                      <strong>Duration:</strong> {duration.toFixed(1)} hours
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Pricing */}
            {currentStep === 4 && (
              <div className="booking-step">
                <h3>Pricing - Cost Breakdown</h3>

                <div className="pricing-summary-card">
                  <div className="pricing-row">
                    <span>Room:</span>
                    <span>{room.name || room.roomName}</span>
                  </div>
                  <div className="pricing-row">
                    <span>Date:</span>
                    <span>{formData.checkInDate || "Not selected"}</span>
                  </div>
                  <div className="pricing-row">
                    <span>Time:</span>
                    <span>
                      {formData.checkInTime} - {formData.checkOutTime}
                    </span>
                  </div>
                  <div className="pricing-row">
                    <span>Duration:</span>
                    <span>{duration.toFixed(1)} hours</span>
                  </div>
                  <div className="pricing-row">
                    <span>Price per Hour:</span>
                    <span>₹{new Intl.NumberFormat("en-IN").format(pricePerHour)}</span>
                  </div>
                  <div className="pricing-row total">
                    <span>Total Price:</span>
                    <span>₹{new Intl.NumberFormat("en-IN").format(totalPrice)}</span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="numberOfGuests">Number of Guests *</label>
                  <select
                    id="numberOfGuests"
                    name="numberOfGuests"
                    value={formData.numberOfGuests}
                    onChange={handleInputChange}
                    className={errors.numberOfGuests ? "error" : ""}
                  >
                    {[1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 40, 50].map((num) => (
                      <option key={num} value={num}>
                        {num} {num === 1 ? "Guest" : "Guests"}
                      </option>
                    ))}
                  </select>
                  {errors.numberOfGuests && (
                    <span className="error-message">{errors.numberOfGuests}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="specialRequests">Special Requests</label>
                  <textarea
                    id="specialRequests"
                    name="specialRequests"
                    value={formData.specialRequests}
                    onChange={handleInputChange}
                    placeholder="Any special requirements or requests?"
                    rows="3"
                  />
                </div>
              </div>
            )}

            {/* Step 5: Payment */}
            {currentStep === 5 && (
              <div className="booking-step">
                <h3>Payment - Payment Method & Confirmation</h3>

                <div className="payment-info-card">
                  <h4>Booking Summary</h4>
                  <div className="summary-item">
                    <span className="label">Guest Name:</span>
                    <span className="value">{formData.userName}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Email:</span>
                    <span className="value">{formData.userEmail}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Phone:</span>
                    <span className="value">{formData.userPhone}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Room:</span>
                    <span className="value">{room.name || room.roomName}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Date:</span>
                    <span className="value">{formData.checkInDate}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Time:</span>
                    <span className="value">
                      {formData.checkInTime} - {formData.checkOutTime}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Guests:</span>
                    <span className="value">{formData.numberOfGuests}</span>
                  </div>
                  <div className="summary-item total">
                    <span className="label">Total Amount:</span>
                    <span className="value">₹{new Intl.NumberFormat("en-IN").format(totalPrice)}</span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="paymentMethod">Payment Method *</label>
                  <select
                    id="paymentMethod"
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleInputChange}
                    className={errors.paymentMethod ? "error" : ""}
                  >
                    <option value="card">Credit/Debit Card</option>
                    <option value="upi">UPI</option>
                    <option value="netbanking">Net Banking</option>
                    <option value="wallet">Digital Wallet</option>
                  </select>
                  {errors.paymentMethod && (
                    <span className="error-message">{errors.paymentMethod}</span>
                  )}
                </div>

                <div className="payment-methods-grid">
                  <div className="payment-method-card">
                    <div className="method-icon">💳</div>
                    <div className="method-name">Credit/Debit Card</div>
                  </div>
                  <div className="payment-method-card">
                    <div className="method-icon">📱</div>
                    <div className="method-name">UPI</div>
                  </div>
                  <div className="payment-method-card">
                    <div className="method-icon">🏦</div>
                    <div className="method-name">Net Banking</div>
                  </div>
                  <div className="payment-method-card">
                    <div className="method-icon">💰</div>
                    <div className="method-name">Digital Wallet</div>
                  </div>
                </div>

                <div className="form-group checkbox">
                  <input
                    type="checkbox"
                    id="agreeToTerms"
                    name="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={handleInputChange}
                    className={errors.agreeToTerms ? "error" : ""}
                  />
                  <label htmlFor="agreeToTerms">
                    I agree to the terms and conditions and cancellation policy *
                  </label>
                  {errors.agreeToTerms && (
                    <span className="error-message">{errors.agreeToTerms}</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="booking-footer">
            <button
              className="btn-secondary"
              onClick={handlePrevious}
              disabled={currentStep === 1}
            >
              Previous
            </button>

            {currentStep < 5 ? (
              <button className="btn-primary" onClick={handleNext}>
                Next
              </button>
            ) : (
              <button
                className="btn-primary btn-confirm"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Processing..." : "Confirm Booking"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
